import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { compact, flatten, isEqual, max, min, omit } from "lodash"
import { countBy, identity, is, isEmpty, map, mergeAll } from "ramda"
import type { CategoriesTableDataItem } from "../components/screens/categories/itemsCategories/table/CategoriesTable"
import { Symbol } from "../constants"
import { formatAsPrice } from "./formatters"
import { pipeline } from "./functional"
import { trimString } from "./general"

export const getUrlWithoutPrefix = (url: Maybe<string>): Maybe<string> => {
    if (!url) {
        return null
    }

    return url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "")
}

export const convertDollarsToCents = (number: Maybe<number>) => {
    if (number == null) {
        return null
    }

    // Backend stores minor units in a bigint column, so this must always be an integer.
    // Math.round avoids float artefacts (e.g. 0.1 * 100 === 10.000000000000002) and sub-cent fractions.
    return Math.round(number * 100)
}

export const convertCentsToDollars = (number: Maybe<number>) => {
    if (number == null) {
        return null
    }

    return number / 100
}

export const getMinMaxInterval = (arrayOfNumbers: number[]): string => {
    return `${formatAsPrice(min(arrayOfNumbers))}${Symbol.MDash}${formatAsPrice(max(arrayOfNumbers))}`
}

export const getHourDuration = (val: number, toFixed: number = 9) => (val / 3600).toFixed(toFixed)

export const recursion =
    (f: (...args: Array<any>) => any) =>
    (...args: Array<any>) =>
        f((...argsi: Array<any>) => recursion(f)(...argsi), ...args)

export const findTableItemById = (
    data: Record<string, any>[],
    id: Maybe<string>,
    idKey: string = "id"
): Maybe<Record<string, any>> => {
    if (!id) {
        return null
    }

    return (
        compact(
            flatten(
                data.map(el => {
                    if (el[idKey] === id) {
                        return el
                    } else if (el.children && el.children.length > 0) {
                        return findTableItemById(el.children, id, idKey)
                    }

                    return false
                })
            )
        )[0] || null
    )
}

export const tableDataLengthChanged = (
    previousData: Record<string, any>[],
    nextData: Record<string, any>[],
    idKey: string = "id"
) => {
    if (nextData.length !== previousData.length) {
        return true
    }

    return nextData.some(el => {
        const prevDataEl = previousData.find(pEl => pEl[idKey] === el[idKey])

        if (prevDataEl) {
            if ((!prevDataEl.children && el.children) || (prevDataEl.children && !el.children)) {
                return true
            }

            if (prevDataEl.children && el.children) {
                if (prevDataEl.children.length !== el.children.length) {
                    return true
                } else {
                    return tableDataLengthChanged(prevDataEl.children, el.children, idKey)
                }
            }

            return false
        }

        return false
    })
}

export const fixTableData = (data: CategoriesTableDataItem[], parentId: Maybe<string> = null) => {
    return data.map((el, index) => {
        return {
            ...el,
            ...(parentId !== undefined
                ? {
                      parentId: parentId,
                  }
                : {}),
            sortIndex: index,
            children: el.children && el.children.length > 0 ? fixTableData(el.children, el.id) : null,
        }
    })
}

export const updateTableItemDataById = (
    data: Record<string, any>[],
    id: string,
    updatedData: Record<string, any>,
    idKey: string = "id"
) => {
    return data.map(el => {
        if (el[idKey] === id) {
            return { ...el, ...updatedData }
        } else {
            return {
                ...el,
                children:
                    el.children && el.children.length > 0
                        ? updateTableItemDataById(el.children, id, updatedData, idKey)
                        : null,
            }
        }
    })
}

export const tableDataChanged = (
    previousData: Record<string, any>[],
    nextData: Record<string, any>[],
    idKey: string = "id"
) => {
    if (nextData.length !== previousData.length) {
        return true
    }

    return nextData.some(el => {
        const prevDataEl = previousData.find(pEl => pEl[idKey] === el[idKey])

        if (prevDataEl) {
            // Compare properties first
            const omitPrevEl = omit(prevDataEl, ["children"])
            const omitNextEl = omit(el, ["children"])

            if (!isEqual(omitPrevEl, omitNextEl)) {
                return true
            }

            if ((!prevDataEl.children && el.children) || (prevDataEl.children && !el.children)) {
                return true
            }

            if (prevDataEl.children && el.children) {
                if (prevDataEl.children.length !== el.children.length) {
                    return true
                } else {
                    return tableDataChanged(prevDataEl.children, el.children, idKey)
                }
            }

            return false
        }

        return true
    })
}

export const sortByFrequencyAndFilter = (items: string[]): string[] =>
    Object.entries(countBy(identity, items))
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .map(item => String(item[0]))

export const searchCaseInsensitive = (search: string, inString: string) =>
    inString.toLowerCase().indexOf(search.toLowerCase()) !== -1

export const trimFormPayload = <T>(values: T): T => {
    return pipeline(
        // @ts-expect-error
        Object.keys(values),
        map((key: string) => trimValue(key, values[key])),
        mergeAll
    ) as T
}

export const trimValue = (key: string, value: any): any => {
    if (typeof value === "string" && value !== "") {
        return { [key]: trimString(value) }
    } else if (is(Array, value) && isEmpty(value)) {
        return { [key]: [] }
    } else if (is(Array, value) && value.length > 0) {
        return { [key]: value.map(v => (typeof v === "string" ? trimString(v) : v)) }
    } else if (is(Object, value)) {
        return { [key]: trimFormPayload(value) }
    } else {
        return { [key]: value }
    }
}

export const trimFormPayloadOld = (values: Record<string, any>): Record<string, any> => {
    // @ts-expect-error
    return pipeline(
        Object.keys(values),
        map((key: string) => {
            const value = values[key]

            if (typeof value === "string" && value !== "") {
                return {
                    [key]: trimString(value),
                }
            } else if (value && value.length === 0) {
                return {
                    [key]: [],
                }
            } else if (value && value.length > 0) {
                return {
                    [key]: value.map(v => {
                        if (typeof v === "string") {
                            return trimString(v)
                        }

                        return v
                    }),
                }
            } else if (value && typeof value === "object") {
                return {
                    [key]: trimFormPayload(value),
                }
            } else {
                return {
                    [key]: value,
                }
            }
        }),
        mergeAll
    )
}

export const arrayValuesDiffers = (array: (string | number)[]) => {
    return array?.some((v, i, a) => v !== a[0])
}
